import { BehaviorSubject, Observable, take, filter } from 'rxjs';

/**
 * @module UserService
 */
function createUserService(apiService, logService, environment, util, _window) {
    const _userService = {};
    const defaultClockValue = 12;
    const userSubject = new BehaviorSubject();
    const rolesSubject = new BehaviorSubject();
    const clockSubject = new BehaviorSubject();
    const uiSettingsSubject = new BehaviorSubject();
    let handle401 = true;
    let rolesApi;
    let userSubjectInProgress;
    let uiSettingsSubjectInProgress;
    let uiSettingsSubscriptionForClock;
    let flagsList = '';

    function log(params) {
        params.module = 'userService';
        logService.log(params);
        return params;
    }

    function logObservable(params) {
        params.module = 'userService';
        return logService.logObservable(params);
    }

    function clear() {
        if (handle401) {
            userSubject.next();
            rolesSubject.next();
            clockSubject.next();
            uiSettingsSubject.next();
            rolesApi = undefined;
        }
    }

    function handleLoginFailure(errorResponse, subscriber, credentials) {
        if (errorResponse?.response?.data?.webBaseUri) {
            // redirect to another data center
            util.submitForm(
                _window,
                {
                    action: errorResponse.response.data.webBaseUri + '/cxs/api-x/redirectPost/login',
                    method: 'POST',
                    values: [
                        {
                            name: 'username',
                            value: credentials.username
                        },
                        {
                            name: 'password',
                            value: credentials.password
                        }
                    ]
                }
            );
            subscriber.error(errorResponse.response);
            return;
        }

        // handle redirect response for "430 Request Header Fields Too Large"
        if (errorResponse?.response?.status === 430) {
            subscriber.error(errorResponse.response);
            return;
        }

        // result error codes can be: auth-frozen, auth-pending, auth-disabled,
        // auth-captcha-required, auth-invlaid, auth-XXX where XXX is a numeric code

        let result = {};
        if (errorResponse?.response?.data) {
            const data = errorResponse.response.data;
            if (data && Array.isArray(data.errors) && data.errors.length > 0) {

                // handle newer style error messages by returning the first error
                result = data.errors[0];

            } else if (data && data.reason) {

                // handle older style error messages by converting to new style
                Object.keys(data).forEach(function(key) {
                    if (key === 'reason') {
                        if (!data.reason.startsWith('auth-')) {
                            result.code = 'auth-' + data.reason;
                        } else {
                            result.code = data.reason;
                        }
                    } else {
                        result[key] = data[key];
                    }
                });

            } else {
                log({
                    code: 'login',
                    descr: 'problem handling login error',
                    trraceId: credentials?.traceId
                });
            }
        }

        subscriber.error(result);
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @returns {Observable<Boolean>} emits only true or false, and only when the value changes
     * @name loggedIn
     * @method
     */
    _userService.loggedIn = function() {
        return apiService.loggedIn();
    };

    /**
     * @param {Object} params
     * @param {Boolean} params.refresh true to refresh data from the server
     * @param {String} params.ignoreAuthModule true if a failure should not redirect
     * @param {String} [params.traceId]
     * @returns {Observable} emits the user
     * @name get
     * @method
     */
    _userService.get = function(params) {
        if (((params?.refresh) || !userSubject.getValue())
                && !userSubjectInProgress
                /*&& loggedIn*/) {
            userSubjectInProgress = true;
            apiService.user.getSelf(params).then(
                result => {
                    userSubjectInProgress = undefined;
                    userSubject.next(result.data);
                },
                () => {
                    userSubjectInProgress = undefined;
                    userSubject.next();
                }
            );
        }
        return userSubject.asObservable();
    };

    /**
     * @param {Object} params
     * @param {Boolean} params.refresh true to refresh data from the server
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the uiSettings change
     * @name getUiSettings
     * @method
     */
    // only used internally within the SDK as of Jan 2023
    _userService.getUiSettings = function(params) {
        if (((params?.refresh) || !uiSettingsSubject.getValue())
                && !uiSettingsSubjectInProgress
                /*&& loggedIn*/) {
            uiSettingsSubjectInProgress = true;
            apiService.uiSettings.getForUser(params).then(
                result => {
                    uiSettingsSubjectInProgress = undefined;
                    if (typeof result.data === 'object') {
                        uiSettingsSubject.next(result.data);
                    } else {
                        uiSettingsSubject.next({});
                    }
                },
                () => {
                    uiSettingsSubjectInProgress = undefined;
                    if (uiSettingsSubject.getValue()) {
                        uiSettingsSubject.next();
                    }
                }
            );
        }
        return uiSettingsSubject.asObservable();
    };

    /**
     * @description saves uiSettings. Warning: this API will modify data across all clients,
     *     so do not change the structure of existing data!
     * @param {Object} params
     * @param {UiSettings} params.delta A UiSettings object that only contains properties
     *     that should be modified
     * @param {String} [params.traceId]
     * @name setUiSettings
     * @method
     */
    // only used internally within the SDK as of Jan 2023
    _userService.setUiSettings = function(params) {
        const settingsParams = util.withTraceId(params);
        _userService.getUiSettings(settingsParams).pipe(util.takeFirstExisting).subscribe({
            next: uiSettings => {
                const toSave = util.merge(util.merge({}, uiSettings), settingsParams.delta);
                apiService.uiSettings.postForUser({ toSave: toSave, traceId: settingsParams?.traceId }).then(
                    () => {
                        _userService.getUiSettings({ refresh: true, traceId: settingsParams?.traceId }).pipe(take(1)).subscribe();
                    },
                    e => log({
                        code: 'setUiSettings',
                        descr: 'not able to post uiSettings',
                        error: e,
                        traceId: e?.traceId
                    })
                );
            },
            error: util.noop
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the clock changes
     * @name getClock
     * @method
     */
    _userService.getClock = function(params) {
        const internalParams = util.withTraceId(params);
        if (!uiSettingsSubscriptionForClock) {
            uiSettingsSubscriptionForClock = _userService.getUiSettings(internalParams).subscribe({
                next: uiSettings => {
                    if (uiSettings) {

                        // if uiSettings already has a valid clock value, use that
                        if ((uiSettings.clock === 12 || uiSettings.clock === 24)) {

                            // if clockSubject doesn't have the correct value, set it.
                            if (uiSettings.clock !== clockSubject.getValue()) {
                                _userService.setUiSettings({
                                    delta: { clock: uiSettings.clock },
                                    traceId: internalParams.traceId
                                });
                                clockSubject.next(uiSettings.clock);
                            }
                            return;
                        }
                        if (_window.Intl && _window.Intl.DateTimeFormat) {
                            // uiSettings has no clock value, so get default based on locale, which is a good guess
                            const localeOptions = _window.Intl.DateTimeFormat(environment.locale, { timeStyle:'medium' }).resolvedOptions();
                            if (localeOptions
                                    && (localeOptions.hour12 || localeOptions.hourCycle === 'h12' || localeOptions.hourCycle === 'h11' )) {
                                clockSubject.next(12);
                                _userService.setUiSettings({
                                    delta: { clock: 12 },
                                    traceId: internalParams.traceId
                                });
                            } else {
                                clockSubject.next(24);
                                _userService.setUiSettings({
                                    delta: { clock: 24 },
                                    traceId: internalParams.traceId
                                });
                            }
                        } else {
                            // legacy browsers can use this default
                            clockSubject.next(defaultClockValue);
                            _userService.setUiSettings({
                                delta: { clock: defaultClockValue },
                                traceId: internalParams.traceId
                            });
                        }
                    } else {
                        if (clockSubject.getValue()) {
                            clockSubject.next();
                        }
                    }
                },
                error: () => {
                    if (clockSubject.getValue()) {
                        clockSubject.next();
                    }
                }
            });
        }
        return clockSubject.asObservable();
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @param {Number} params.value 12 or 24 are the only accepted values
     * @name setClock
     * @method
     */
    _userService.setClock = function(params) {
        const clockParams = util.withTraceId(params);
        if (clockParams.value !== 12 && clockParams.value !== 24) {
            log({
                code: 'setClock',
                descr: 'tried to set clock to invalid value',
                object: clockParams.value,
                traceId: clockParams.traceId
            });
            return;
        }

        _userService.getUiSettings({ traceId: clockParams.traceId }).pipe(util.takeFirstExisting).subscribe(uiSettings => {
            // save the new clock setting if needed
            if (uiSettings.clock !== params?.value) {
                _userService.setUiSettings({ delta: { clock: params.value }, traceId: clockParams.traceId });
            }
            // update the subject if needed
            if (clockSubject.getValue() !== params.value) {
                clockSubject.next(params.value);
            }
        });
    };

    /**
     * @returns {Observable}
     * @name logout
     * @method
     */
    _userService.logout = function() {
        return new Observable(subscriber => {
            apiService.auth.logout().then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} credentials Will use username/password first if given,
     *     or if not, then sid
     * @param {String} credentials.username
     * @param {String} credentials.password
     * @param {String} credentials.sid
     * @param {String} credentials.traceId
     * @returns {Observable} emits after successful login
     * @name login
     * @method
     */
    _userService.login = function(credentials) {
        return new Observable(subscriber => {
            if (credentials?.username && credentials?.password) {
                apiService.auth.login(credentials).then(
                    () => {
                        subscriber.next();
                        subscriber.complete();
                    },
                    errorResponse => {
                        handleLoginFailure(errorResponse, subscriber, credentials);
                    }
                );
            } else if (credentials?.sid) {
                apiService.auth.loginSid(credentials).then(
                    () => {
                        subscriber.next();
                        subscriber.complete();
                    },
                    e => subscriber.error(
                        log({
                            code: 'loginSidFailed',
                            traceId: e?.traceId,
                            error: e
                        })
                    )
                );
            } else {
                subscriber.error(
                    // note: do not log credentials!
                    log({
                        code: 'loginBadParams',
                        traceId: credentials?.traceId
                    })
                );
            }
        });
    };

    /**
     * @param {Object} signupData
     * @param {String} signupData.username
     * @param {String} signupData.email
     * @param {String} signupData.password
     * @param {String} signupData.traceId
     * @returns {Observable<SignupResult>}
     * @name signup
     * @method
     */
    _userService.signup = function(signupData) {
        return new Observable(subscriber => {
            const params = util.withTraceId(signupData);
            apiService.auth.signup(params).then(
                result => {
                    _userService.get({ refresh: true, traceId: params.traceId }).pipe(take(1)).subscribe();
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                errorResponse => {

                    // result error codes can be: password-req, username-req, email-invalid,
                    // username-exists, nonesupported-character, password, username, email

                    let result = {};
                    if (errorResponse?.response?.data) {
                        const data = errorResponse.response.data;
                        if (data && Array.isArray(data.errors) && data.errors.length > 0) {

                            // handle newer style error messages by returning the first error
                            result = data.errors[0];

                        } else if (data && data.reason) {

                            // handle older style error messages by converting to new style
                            Object.keys(data).forEach(function(key) {
                                if (key === 'reason') {
                                    result.code = data.reason;
                                } else {
                                    result[key] = data[key];
                                }
                            });

                        } else {
                            log({
                                code: 'signup',
                                descr: 'problem handling signup error',
                                traceId: errorResponse?.traceId
                            });
                        }
                    }
                    subscriber.error(result);
                }
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable<Roles>} An Observable emitting an Object containing all the roles
     *     a user could have, along with permissions for each role.
     * @name roles
     * @method
     */
    _userService.roles = function(params) {
        if (!rolesApi) {
            rolesApi = true;
            apiService.accessControl.roles(params).then(
                rolesResult => {
                    const roles = {};

                    // add new roles, but convert permissions from array to object
                    rolesResult.data.items.forEach(role => {
                        roles[role.id] = role;
                        const permissionsObj = {};
                        if (Array.isArray(role.permissions)) {
                            role.permissions.forEach(permission => {
                                permissionsObj[permission.id] = {};
                            });
                        }
                        role.permissions = permissionsObj;
                        role.name ??= role.id;
                    });

                    // add the "none" role. Clients can set name and descr to localized values
                    let maxSort = 0;
                    Object.keys(roles).forEach(key => {
                        maxSort = Math.max(maxSort, roles[key].sort);
                    });
                    roles.none = {
                        id: 'none',
                        name: '',
                        descr: '',
                        permissions: {},
                        sort: maxSort + 1,
                        status: 'A'
                    };

                    rolesSubject.next(roles);
                },
                util.noop
            );
        }
        return rolesSubject.asObservable();
    };

    /**
     * @param {Object} params
     * @param {String} params.userId
     * @param {String} params.token
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name verifyEmail
     * @method
     */
    _userService.verifyEmail = function(params) {
        return new Observable(subscriber => {
            apiService.user.emailVerification(util.withTraceId(params)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                (e) => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable<Array<OauthProvider>>}
     * @name getOauthProviders
     * @method
     */
    _userService.getOauthProviders = function(params) {
        return new Observable(subscriber => {
            apiService.auth.loadOauthProviderList(util.withTraceId(params)).then(
                result => {
                    if (result && result.data && Array.isArray(result.data.items)) {
                        subscriber.next(result.data.items);
                        subscriber.complete();
                    } else {
                        subscriber.error();
                    }
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.providerId
     * @param {String} [params.traceId]
     * @returns {Observable<OauthProvider>}
     * @name getOauthProvider
     * @method
     */
    _userService.getOauthProvider = function(params) {
        return new Observable(subscriber => {
            apiService.oauthClient.getOauthProvider(util.withTraceId(params)).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.providerId
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name getSocialMediaAccessToken
     * @method
     */
    _userService.getSocialMediaAccessToken = function(params) {
        return new Observable(subscriber => {
            apiService.oauthClient.getSocialMediaAccessToken(util.withTraceId(params)).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} userInfo
     * @param {String} userInfo.email
     * @param {String} userInfo.traceId
     * @returns {Observable}
     * @name forgotUsername
     * @method
     */
    _userService.forgotUsername = function(userInfo) {
        return new Observable(subscriber => {
            apiService.auth.forgotUsername(util.withTraceId(userInfo)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} userInfo
     * @param {String} userInfo.username
     * @param {String} userInfo.email
     * @param {String} userInfo.traceId
     * @returns {Observable} Could emit a redirect, success or failure
     * @name forgotPassword
     * @method
     */
    _userService.forgotPassword = function(userInfo) {
        return new Observable(subscriber => {
            apiService.auth.forgotPassword(util.withTraceId(userInfo)).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} resetPasswordData
     * @param {String} resetPasswordData.username
     * @param {String} resetPasswordData.password
     * @param {String} resetPasswordData.token
     * @param {String} resetPasswordData.traceId
     * @returns {Observable}
     * @name requestResetPassword
     * @method
     */
    _userService.requestResetPassword = function(resetPasswordData) {
        return new Observable(subscriber => {
            apiService.auth.requestResetPassword(util.withTraceId(resetPasswordData)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.token
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name verifyResetPassword
     * @method
     */
    _userService.verifyResetPassword = function(params) {
        return new Observable(subscriber => {
            apiService.auth.verifyResetPassword(util.withTraceId(params)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.response_type
     * @param {String} params.client_id
     * @param {String} params.redirect_uri
     * @param {String} params.scope
     * @param {String} params.state
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name urlFromGrantCode
     * @method
     */
    _userService.urlFromGrantCode = function(params) {
        return new Observable(subscriber => {
            const grantParams = util.withTraceId(params);
            apiService.auth.grantCode(grantParams).then(
                grantCodeResult => {
                    function emitResult() {
                        if (grantCodeResult && typeof grantCodeResult.data === 'object') {
                            let url = new URL(grantParams.redirect_uri);
                            // keys should be "code" and "state"
                            Object.keys(grantCodeResult.data).forEach(key => {
                                url.searchParams.append(key, grantCodeResult.data[key]);
                            });
                            subscriber.next(url);
                            subscriber.complete();
                        } else {
                            subscriber.error({
                                code: 'grantCode',
                                descr: 'unexpected result',
                                traceId: grantCodeResult?.traceId
                            });
                        }
                    }

                    // wait for log to be successful before emitting
                    // since clients will redirect after this.
                    logObservable({
                        type: 'info',
                        code: 'grantCode',
                        object: grantCodeResult,
                        traceId: grantCodeResult?.traceId
                    }).subscribe({
                        next: emitResult,
                        error: emitResult
                    });
                },
                grantCodeErrorResult => {
                    log({
                        code: 'grantCode',
                        error: grantCodeErrorResult,
                        traceId: grantCodeErrorResult?.traceId
                    });
                    let state;
                    if (grantCodeErrorResult.config
                            && grantCodeErrorResult.config.oauthData
                            && grantCodeErrorResult.config.oauthData.state) {
                        state = grantCodeErrorResult.config.oauthData.state;
                    }
                    subscriber.error({
                        error: grantCodeErrorResult.response.data.error,
                        state: state,
                        traceId: grantCodeErrorResult?.traceId
                    });
                }
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.response_type
     * @param {String} params.client_id
     * @param {String} params.redirect_uri
     * @param {String} params.scope
     * @param {String} params.state
     * @param {String} [params.traceId]
     * @returns {Observable}
     * @name urlFromGrantImplicit
     * @method
     */
    _userService.urlFromGrantImplicit = function(params) {
        return new Observable(subscriber => {
            const grantParams = util.withTraceId(params);
            apiService.auth.grantImplicit(grantParams).then(
                grantImplicitResult => {
                    function emitResult() {
                        if (grantImplicitResult && typeof grantImplicitResult.data === 'object') {
                            let url = new URL(grantParams.redirect_uri);
                            Object.keys(grantImplicitResult.data).forEach(key => {
                                url.searchParams.append(key, grantImplicitResult.data[key]);
                            });
                            subscriber.next(url);
                            subscriber.complete();
                        } else {
                            subscriber.error({
                                code: 'grantImplicit',
                                descr: 'unexpected result',
                                traceId: grantImplicitResult?.traceId
                            });
                        }
                    }

                    // wait for log to be successful before emitting
                    // since clients will redirect after this.
                    logObservable({
                        type: 'info',
                        code: 'grantImplicit',
                        object: grantImplicitResult,
                        traceId: grantImplicitResult?.traceId
                    }).subscribe({
                        next: emitResult,
                        error: emitResult
                    });
                },
                grantImplicitErrorResult => {
                    log({
                        code: 'grantImplicit',
                        error: grantImplicitErrorResult,
                        traceId: grantImplicitErrorResult?.traceId
                    });
                    subscriber.error({
                        error: grantImplicitErrorResult,
                        traceId: grantImplicitErrorResult?.traceId
                    });
                }
            );
        });
    };

    /**
     * @param {Object} changePasswordData
     * @param {String} changePasswordData.oldPassword
     * @param {String} changePasswordData.newPassword
     * @param {String} changePasswordData.traceId
     * @returns {Observable}
     * @name changePassword
     * @method
     */
    _userService.changePassword = function(changePasswordData) {
        return new Observable(subscriber => {
            apiService.auth.changePassword(util.withTraceId(changePasswordData)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the request is complete
     * @name resendEmailVerification
     * @method
     */
    _userService.resendEmailVerification = function(params) {
        return new Observable(subscriber => {
            apiService.user.resendEmailVerification(util.withTraceId(params)).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    /**
     * @param {*} value
     * @name handle401
     * @method
     */
    _userService.handle401 = function(value) {
        handle401 = Boolean(value);
    };

    /**
     * @param {Object} params
     * @param {String} params.oauthClientId OAuth client ID
     * @param {String} [params.traceId]
     * @returns {Observable<OauthConsumerData>}
     * @name loadOauthConsumer
     * @method
     */
    _userService.loadOauthConsumer = function(params) {
        return new Observable(subscriber => {
            apiService.auth.loadOauthConsumer(util.withTraceId(params)).then(
                oauthConsumerResult => {
                    subscriber.next(oauthConsumerResult.data);
                    subscriber.complete();
                },
                e => {
                    subscriber.error(e);
                }
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable<Array<OauthConsumerData>>}
     * @name getOauthConsumers
     * @method
     */
    _userService.getOauthConsumers = function(params) {
        return new Observable(subscriber => {
            apiService.auth.getOauthConsumers(util.withTraceId(params)).then(
                oauthConsumerResult => {
                    subscriber.next(oauthConsumerResult.data);
                    subscriber.complete();
                },
                e => {
                    subscriber.error(e);
                }
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable<Array<ConsumerAccessToken>>}
     * @name getConsumerAccessTokens
     * @method
     */
    _userService.getConsumerAccessTokens = function(params) {
        return new Observable(subscriber => {
            apiService.auth.getConsumerAccessTokens(util.withTraceId(params)).then(
                function(oauthConsumerResult) {
                    subscriber.next(oauthConsumerResult.data);
                    subscriber.complete();
                },
                e => {
                    subscriber.error(e);
                }
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable<Array<SocialMediaAccessToken>>}
     * @name getSocialMediaAccessTokenList
     * @method
     */
    _userService.getSocialMediaAccessTokenList = function(params) {
        return new Observable(subscriber => {
            apiService.oauthClient.getSocialMediaAccessTokenList(util.withTraceId(params)).then(
                tokenResult => {
                    if (Array.isArray(tokenResult?.data?.items)) {
                        subscriber.next(tokenResult.data.items);
                        subscriber.complete();
                    } else {
                        subscriber.error();
                    }
                },
                e => {
                    subscriber.error(e);
                }
            );
        });
    };

    /**
     * @param {String} guestCode - guest code used on subsequent API calls
     * @name setGuestCode
     * @method
     */
    _userService.setGuestCode = function(guestCode) {
        apiService.setGuestCode(guestCode);
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable} emits an object with keys that are locale codes, for example: en-US
     * @name getLocales
     * @method
     */
    _userService.getLocales = function(params) {
        return new Observable(subscriber => {
            apiService.getLocales(util.withTraceId(params)).then(
                localesResult => {
                    subscriber.next(localesResult.data.items);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.locale examples: en-US, es-MX
     * @param {String} [params.traceId]
     * @returns {Observable} emits an localization data
     * @name getLocaleStrings
     * @method
     */
    _userService.getLocaleStrings = function(params) {
        return new Observable(subscriber => {
            apiService.getLocalizations(util.withTraceId(params)).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @param {String} flag a flag to be checked
     * @returns {Boolean} true if the flag is active
     * @name hasFlag
     * @method
     */
    _userService.hasFlag = function(flag) {
        return flagsList.includes(flag);
    };

    /**
     * @param {String} flags a delimited list of flags that are set for the user
     * @name setFlags
     * @method
     */
    _userService.setFlags = function(flags) {
        if (flags === undefined || flags === null || flags === '') {
            flagsList = '';
        } else if (typeof flags === 'string') {
            flagsList = util.removeInvalidCharacters(flags);
        }
    };

    _userService.resetAuth = function(params) {
        if (params?.oauthToken) {
            environment.oauthToken = params?.oauthToken;
            apiService.resetAuth(params);
        } else if (params?.sessionId) {
            environment.sessionId = params?.sessionId;
            apiService.resetAuth(params);
        }
    };

    return Object.freeze(_userService);
}

export default createUserService;
